<template>
  <div class="new-criterio-details" :class="{ 'with-limiter': limiterActive }">
    <div class="d-flex justify-space-between buttons">
      <mf-tabs-button v-model="filterType" size="sm" :buttons="toggleButtons" />
      <segmentation-limiter
        ref="segmentationLimiter"
        :disabled="viewOnly"
        :segment="segment"
        :segment-original-size="segmentOriginalSize"
        :is-segmentation-without-rfv="isSegmentationWithoutRfv"
        @active-change="onLimiterChange"
      />
    </div>
    <div v-show="filterType === 'group'" class="criterio-details-content">
      <div v-for="(group, groupIndex) in segmentationGroups.groups" :key="`${groupIndex}group`">
        <v-card class="drag-container">
          <v-row>
            <span class="group-title">Grupo {{ groupIndex + 1 }}</span>
            <v-spacer />
            <mf-button
              v-if="!disableDelete"
              color="cinza-0"
              icon="close"
              type="icon"
              size="sm"
              :class="[selectedCriteria ? 'hidden-close' : '']"
              @click="deleteSegmentationGroup(groupIndex)"
            />
          </v-row>

          <div v-for="(criteria, criteriaIndex) in group.criteria" :key="`${criteriaIndex}criteria`">
            <div
              :id="`group${groupIndex}criteria${criteriaIndex}`"
              class="dragged-criteria"
              :class="[
                Boolean(selectedCriteria)
                  ? selectedCriteria.groupIndex === groupIndex && selectedCriteria.criteriaIndex === criteriaIndex
                    ? 'editing-criteria'
                    : 'editing-another-criteria'
                  : 'selecting-criteria',
              ]"
              @click="localSelectCriteria({ groupIndex, criteriaIndex, newCriteria: false }, $event)"
            >
              <v-row class="align-center">
                <v-flex>
                  <span class="criterio-title">{{ criterioTitle(criteria) }}</span>
                </v-flex>
                <div class="d-flex align-center ml-auto remove-criteria mr-2">
                  <copy-criteria-button :view-only="viewOnly" :group-index="groupIndex" :criteria-index="criteriaIndex" />
                  <mf-button
                    color="cinza-0"
                    icon="close"
                    type="icon"
                    size="sm"
                    :class="[selectedCriteria ? 'hidden-close' : '']"
                    :disabled="viewOnly"
                    @click="deleteSegmentationCriteria({ groupIndex, criteriaIndex })"
                  />
                </div>
              </v-row>
              <v-row>
                <v-flex>
                  <span>
                    <segmentation-text :criteria="criteria" :group-index="groupIndex" :criteria-index="criteriaIndex" />
                  </span>
                </v-flex>
              </v-row>
            </div>

            <div v-if="group.criteria.length >= 1" class="group-condition">
              <v-btn
                small
                class="elevation-0 operator-button"
                :disabled="viewOnly"
                :class="group.operator === 'AND' ? 'selected-operator' : ''"
                @click="setGroupOperator({ groupIndex, operator: 'AND' })"
                >E</v-btn
              >
              <v-btn
                small
                class="elevation-0 operator-button"
                :disabled="viewOnly"
                :class="group.operator === 'OR' ? 'selected-operator' : ''"
                @click="setGroupOperator({ groupIndex, operator: 'OR' })"
                >Ou</v-btn
              >
            </div>
          </div>

          <div
            v-if="!viewOnly"
            :id="`${groupIndex}-${group.criteria.length}`"
            class="drag-box"
            :class="{ over: toDropId === `${groupIndex}-${group.criteria.length}` }"
            @drop="drop($event, groupIndex, group.criteria.length)"
            @dragenter="dragEnter"
            @dragleave="dragLeave"
            @dragover="dragOver"
          >
            <span class="drag-text">Arraste ou de um duplo-clique em um item ao lado para adicionar o critério ao grupo</span>
          </div>
        </v-card>

        <div v-if="segmentationGroups.groups.length >= 2" class="group-condition">
          <v-btn
            small
            class="elevation-0 operator-button"
            :disabled="viewOnly"
            :class="segmentationGroups.operator === 'AND' ? 'selected-operator' : ''"
            @click="setSegmentationOperator({ operator: 'AND' })"
            >E</v-btn
          >
          <v-btn
            small
            class="elevation-0 operator-button"
            :disabled="viewOnly"
            :class="segmentationGroups.operator === 'OR' ? 'selected-operator' : ''"
            @click="setSegmentationOperator({ operator: 'OR' })"
            >Ou</v-btn
          >
        </div>
      </div>

      <v-card v-if="!Boolean(segmentationGroups.groups && segmentationGroups.groups.length)" class="drag-container">
        <v-row>
          <span class="group-title">Grupo 1</span>
        </v-row>
        <div
          v-if="!viewOnly"
          id="drag-box"
          class="drag-box"
          :class="{ over: toDropId === 'drag-box' }"
          @drop="drop($event, segmentationGroups.groups.length, 0)"
          @dragenter="dragEnter"
          @dragleave="dragLeave"
          @dragover="dragOver"
        >
          <span class="drag-text">Arraste ou de um duplo-clique em um item ao lado para adicionar o critério ao grupo</span>
        </div>
      </v-card>

      <div v-if="Boolean(segmentationGroups.groups && segmentationGroups.groups.length)" class="text-center add-new-group">
        <mf-button id="button" :disabled="viewOnly" text label="Adicionar grupo" icon="add" @click="createNewGroup" />
      </div>
    </div>
    <segmentation-map v-if="filterType === 'map'" class="criterio-details-content" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import cloneDeep from 'lodash/cloneDeep'
export default {
  components: {
    SegmentationText: () => import('@/modules/clientes/segmentation/views/components/SegmentationText.vue'),
    SegmentationMap: () => import('@/modules/clientes/segmentation/views/components/SegmentationMap.vue'),
    CopyCriteriaButton: () => import('@/modules/clientes/segmentation/views/components/CopyCriteriaButton.vue'),
    SegmentationLimiter: () => import('@/modules/clientes/segmentation/views/components/SegmentationLimiter.vue'),
  },
  props: {
    viewOnly: {
      type: Boolean,
      default: false,
    },
    segment: {
      type: Object,
      default: () => {},
    },
    segmentOriginalSize: {
      type: Number,
      default: null,
    },
    isSegmentationWithoutRfv: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['filter-type-changed'],
  data: () => ({
    toDropId: '',
    groupOperator: 0,
    filterType: 'group',
    limiterActive: false,
  }),
  computed: {
    ...mapState({
      draggedObject: (state) => state.segmentation.draggedObject,
      selectedCriteria: (state) => state.segmentation.selectedCriteria,
      segmentationGroups: (state) => state.segmentation.segmentationGroups,
      segmentationIsValid: (state) => state.segmentation.segmentationIsValid,
      segmentation: (state) => state.segmentation.segmentation,
    }),
    disableDelete() {
      return this.viewOnly || this.segmentationGroups.groups.length === 1
    },
    pageTitle() {
      const type = this.filterType === 'group' ? 'Critérios' : 'Mapa'
      return `${type} da segmentação`
    },
    toggleButtons() {
      return [
        {
          id: 0,
          label: 'Grupos',
          value: 'group',
          active: true,
        },
        {
          id: 1,
          label: 'Mapa',
          value: 'map',
          active: true,
        },
      ]
    },
  },

  watch: {
    selectedCriteria() {
      if (this.selectedCriteria) this.scrollMeTo()
    },
    filterType(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('filter-type-changed', newValue)
      }
    },
  },
  async mounted() {
    await this.getUnitCoordinates({ unidades: '' })
  },
  destroyed() {
    this.setSelectedCriteria(null)
  },
  methods: {
    ...mapActions([
      'setSelectedCriteria',
      'setSegmentationGroups',
      'deleteSegmentationCriteria',
      'deleteSegmentationGroup',
      'selectCriteria',
      'setGroupOperator',
      'setSegmentationOperator',
      'setPersistOriginalSize',
    ]),
    ...mapWaitingActions({
      getUnitCoordinates: 'unit-coordinates',
    }),
    onLimiterChange(isActive) {
      this.limiterActive = !!isActive
    },
    localSelectCriteria({ groupIndex, criteriaIndex, ...args }, event) {
      // Check if click target is within the remove-criteria area
      if (event && event.target.closest('.remove-criteria')) {
        return
      }

      const currentCriteria = this.segmentationGroups.groups[groupIndex].criteria[criteriaIndex]
      const tab = this.segmentation.children.find((node) => node.id === currentCriteria[0].id)
      const group = tab.children.find((node) => node.id === currentCriteria[1].id)
      if (!group.requires_pii_access || this.hasPiiAccess) {
        this.selectCriteria({ groupIndex, criteriaIndex, ...args })
      }
    },
    drop(evt, groupIndex, criteriaIndex) {
      if (this.draggedObject.criteriaValue) {
        this.setSelectedCriteria({ ...this.draggedObject, groupIndex, criteriaIndex })
        this.insertCriteriaOnGroup(this.draggedObject.criteriaValue, groupIndex, criteriaIndex)
        this.setPersistOriginalSize(false)
      }
      this.dragLeave()
    },
    dragEnter(evt) {
      this.toDropId = evt.toElement.id
    },
    dragLeave() {
      this.toDropId = ''
    },
    dragOver(evt) {
      this.toDropId = evt.toElement.id
      evt.preventDefault()
    },

    scrollMeTo() {
      clearTimeout(this.timeout)

      const elementToScroll = '#group' + this.selectedCriteria.groupIndex + 'criteria' + this.selectedCriteria.criteriaIndex
      this.timeout = setTimeout(() => {
        const ell = document.querySelector(elementToScroll)

        this.$vuetify.goTo(ell, { container: '#criteria-group' })
      }, 200)
    },

    insertCriteriaOnGroup(branch, groupIndex, criteriaIndex) {
      let segmentationGroups = cloneDeep(this.segmentationGroups)
      if (groupIndex >= segmentationGroups.groups.length) {
        segmentationGroups.groups[groupIndex] = {
          criteria: [],
          operator: 'AND',
        }
      }

      segmentationGroups.groups[groupIndex].criteria[criteriaIndex] = branch
      this.setSegmentationGroups(segmentationGroups)
    },

    createNewGroup() {
      let segmentationGroups = cloneDeep(this.segmentationGroups)

      segmentationGroups.groups.push({
        criteria: [],
        operator: 'AND',
      })

      this.setSegmentationGroups(segmentationGroups)
    },

    criterioTitle(criteria) {
      let criteriaToRender = criteria
      let currentNode = this.segmentation

      if (this.selectedCriteria && this.groupIndex === this.selectedCriteria.groupIndex && this.criteriaIndex === this.selectedCriteria.criteriaIndex) {
        criteriaToRender = this.selectedCriteria.criteriaValue
      }

      criteriaToRender = criteriaToRender.map((criteria, index) => {
        return (currentNode = cloneDeep(currentNode?.children?.find((node) => node.id === criteria.id)))
      })

      // O nível 0 é a aba
      // O nível 1 é o grupo
      // O nível 2 é o título do critério
      return criteriaToRender[2].label
    },
  },
}
</script>

<style lang="scss" scoped>
.new-criterio-details {
  position: relative;

  .criterio-details-content {
    width: 100%;
    max-height: 500px;
    overflow-y: auto;
    overflow-x: hidden;
    transition: width 200ms ease-in;
  }
  &.with-limiter {
    .criterio-details-content {
      width: calc(100% - 320px);
    }
  }
  .drag-container {
    padding: 10px 20px 20px 20px;
    border-radius: 10px;
    margin-bottom: 30px;
    .row {
      margin-left: 2px;
    }
  }
  .group-title {
    margin-bottom: 5px;
    font-weight: bold;
  }
  .criterio-title {
    font-weight: bold;
    font-size: 14px;
  }
  .drag-box {
    padding: 30px;
    border-radius: 5px;
    border: 1px dashed $marca-0-l40;
    text-align: center;
    * {
      pointer-events: none;
    }
    &.over {
      background-color: $marca-0-l90;
      transition: all 200ms;
    }
  }
  .drag-text {
    color: $marca-0-l40;
    font-size: 12px;
  }
  .dragged-criteria {
    border-radius: 5px;
    margin-bottom: 20px;

    background-color: $cinza-l90;
    padding: 10px;
    font-size: 16px;
    color: $cinza-d20;
    border: 1px $cinza-l90 solid;
  }
  .editing-criteria {
    background-color: $ilustracao-solid-6;
    border: 1px $marca-1 solid;
  }
  .editing-another-criteria {
    cursor: not-allowed;
  }
  .selecting-criteria {
    cursor: pointer;
  }
  .group-condition {
    margin-bottom: 20px;
    button {
      margin: 0;
      min-width: 40px;
    }
    .operator-button {
      background-color: $branco !important;
      border: 2px $cinza-l70 solid !important;
      color: rgba($color: #1c1c1c, $alpha: 0.37);
    }
    .selected-operator {
      &:not(.v-btn--disabled) {
        background-color: $marca-0-l90 !important;
        border: 2px $marca-0-l40 solid !important;
      }
    }
  }
  .remove-criteria {
    text-align: center;
    font-weight: bold;
  }
  .add-new-group {
    i {
      margin-top: 1px;
    }
  }
  .hidden-close {
    display: none;
  }
  .buttons {
    margin: 6px 0px 18px 0px;
  }

  .buttons ::v-deep .theme--light.v-btn-toggle:not(.v-btn-toggle--group) {
    background-color: transparent;
  }
}
</style>
